import * as React from 'react';

import formatClassroomsPostData from './format_classrooms_post_data';

import { requestPost, } from '../../../../../modules/request';
import {
  DataTable,
  DateTimePickerInput,
  Snackbar,
  clearAssignmentFlowLocalStorage,
  defaultSnackbarTimeout,
  getExtraSmallColoredIconForActivityClassification,
  getPageTitle,
  ASSIGNED_CLASSROOMS,
  UNIT_TEMPLATE_ID,
} from '../../../../Shared/index';
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor';
import ScrollToTop from '../../shared/scroll_to_top';
import {
  activityTableHeader,
  previouslyAssignedTableHeader,
  publishDateTableHeader,
  renderToolIcon,
  requestPreviouslyAssignedActivityData,
  toolTableHeader,
} from '../activity_table_elements';
import AssignmentFlowNavigation from '../assignment_flow_navigation';
import PreviouslyAssignedTooltip from '../previouslyAssignedTooltip';

const DIAGNOSTIC_CLASSIFICATION_ID = 4
const MY_DIAGNOSTICS_PATH = '/teachers/progress_reports/diagnostic_reports/diagnostics'
const ASSIGN_RECOMMENDATIONS_SUPPORT_URL = 'https://support.quill.org/en/articles/1589522-how-do-i-assign-diagnostic-recommendations'
const SCHEDULING_ERROR_SNACKBAR_COPY = 'Something went wrong scheduling the growth diagnostic. Please try again, or contact support if the problem continues.'

const tableHeaders = [
  { ...toolTableHeader, width: '60px' },
  { ...activityTableHeader, width: '491px' },
  { ...publishDateTableHeader, width: '491px' },
  previouslyAssignedTableHeader,
]

interface GrowthDiagnostic {
  activityId: number;
  name: string;
  unitTemplateId: number;
}

interface GrowthDiagnosticFollowupProps {
  activities?: any[];
  assignedClassrooms: any[];
  assignedUnitTemplateId: number | string;
  growthDiagnostic: GrowthDiagnostic;
}

const GrowthDiagnosticFollowup: React.FC<GrowthDiagnosticFollowupProps> = ({
  activities,
  assignedClassrooms,
  assignedUnitTemplateId,
  growthDiagnostic,
}) => {
  document.title = getPageTitle('Assign', 'Schedule the Growth Diagnostic')

  const [publishDate, setPublishDate] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [previouslyAssignedActivityData, setPreviouslyAssignedActivityData] = React.useState(null);
  const [snackbarVisible, setSnackbarVisible] = React.useState(false);

  useSnackbarMonitor(snackbarVisible, setSnackbarVisible, defaultSnackbarTimeout);

  React.useEffect(() => {
    window.localStorage.setItem(UNIT_TEMPLATE_ID, String(assignedUnitTemplateId));
    window.localStorage.setItem(ASSIGNED_CLASSROOMS, JSON.stringify(assignedClassrooms));
    requestPreviouslyAssignedActivityData([growthDiagnostic.activityId], setPreviouslyAssignedActivityData)
  }, [])

  function goToDashboard() {
    window.location.href = `${window.postbuildEnv.DEFAULT_URL}/teachers/classrooms/dashboard`;
  }

  function handleSkip() {
    clearAssignmentFlowLocalStorage();
    goToDashboard();
  }

  function handleAssign() {
    if (!publishDate || submitting) { return }

    setSubmitting(true);
    requestPost(
      '/teachers/units/assign_growth_diagnostic',
      {
        activity_id: growthDiagnostic.activityId,
        unit_template_id: growthDiagnostic.unitTemplateId,
        publish_date: publishDate,
        classrooms: formatClassroomsPostData(assignedClassrooms),
      },
      () => {
        clearAssignmentFlowLocalStorage();
        goToDashboard();
      },
      () => {
        setSubmitting(false);
        setSnackbarVisible(true);
      }
    );
  }

  function tableRow() {
    const growthActivity = activities?.find(a => Number(a.id) === growthDiagnostic.activityId)

    const toolIcon = growthActivity
      ? renderToolIcon(growthActivity)
      : getExtraSmallColoredIconForActivityClassification(DIAGNOSTIC_CLASSIFICATION_ID)

    const activityName = <span>{growthDiagnostic.name}</span>

    const publishDatePicker = (
      <DateTimePickerInput
        closeFunction={setPublishDate}
        defaultText="Select date"
        initialValue={publishDate}
        keyForElement={`publishDate${growthDiagnostic.activityId}`}
      />
    );

    return {
      id: growthDiagnostic.activityId,
      tool: toolIcon,
      activity: activityName,
      publishDatePicker,
      previouslyAssigned: (
        <PreviouslyAssignedTooltip previouslyAssignedActivityData={previouslyAssignedActivityData?.[growthDiagnostic.activityId]} />
      ),
    }
  }

  function renderButtons() {
    let assignButtonClass = 'quill-button medium contained green focus-on-light'
    const assignButtonDisabled = !publishDate || submitting
    if (assignButtonDisabled) { assignButtonClass += ' disabled' }

    return (
      <div className="followup-buttons">
        <button className="quill-button medium outlined green focus-on-light" onClick={handleSkip} type="button">Skip for now</button>
        <button aria-disabled={assignButtonDisabled} className={assignButtonClass} onClick={handleAssign} type="button">Schedule Growth Diagnostic</button>
      </div>
    )
  }

  return (
    <div>
      <Snackbar text={SCHEDULING_ERROR_SNACKBAR_COPY} visible={snackbarVisible} />
      <ScrollToTop />
      <AssignmentFlowNavigation button={renderButtons()} />
      <main className="name-and-assign-activity-pack growth-diagnostic-followup">
        <div className="container">
          <h1 className="assign-header">You&#39;ve assigned a baseline diagnostic! Here&#39;s what comes next.</h1>
          <section aria-labelledby="encourageStudentsHeader" className="assignment-section">
            <span className="step-chip">Do now</span>
            <div className="assignment-section-header">
              <span aria-hidden="true" className="assignment-section-number">1</span>
              <h2 className="assignment-section-name" id="encourageStudentsHeader">Encourage your students to complete the baseline diagnostic</h2>
            </div>
            <p className="section-instructions">See which students have completed the baseline diagnostic in the <a className="focus-on-light" href={MY_DIAGNOSTICS_PATH}>My Diagnostics</a> report.</p>
          </section>
          <section aria-labelledby="assignRecommendationsHeader" className="assignment-section">
            <span className="step-chip">Once students complete baseline diagnostic</span>
            <div className="assignment-section-header">
              <span aria-hidden="true" className="assignment-section-number">2</span>
              <h2 className="assignment-section-name" id="assignRecommendationsHeader">Assign practice recommendations after reviewing students&#39; baseline diagnostic results</h2>
            </div>
            <p className="section-instructions">When a student completes a baseline diagnostic, Quill curates a personalized plan of practice recommendations for the student.<br /><a aria-label="Learn how to assign your students' practice recommendations (opens in a new tab)" className="focus-on-light" href={ASSIGN_RECOMMENDATIONS_SUPPORT_URL} rel="noopener noreferrer" target="_blank">Learn how to assign your students&#39; practice recommendations</a>.</p>
          </section>
          <section aria-labelledby="scheduleGrowthDiagnosticHeader" className="assignment-section">
            <span className="step-chip">Schedule now, complete later</span>
            <div className="assignment-section-header">
              <span aria-hidden="true" className="assignment-section-number">3</span>
              <h2 className="assignment-section-name" id="scheduleGrowthDiagnosticHeader">Schedule the growth diagnostic</h2>
            </div>
            <p className="section-instructions">Quill recommends saving time and scheduling the growth diagnostic now to ensure you&#39;ll have meaningful growth data showing all the skills your students have learned. We recommend selecting a publish date that gives students ample time to complete their practice recommendations. Many teachers whose school years span from August to May typically assign the growth diagnostic in March or April, before end-of-year assessments.</p>
            <div className="assignment-section-body">
              <DataTable
                headers={tableHeaders}
                reportingFormat={true}
                rows={[tableRow()]}
              />
            </div>
            <p className="section-instructions">When you schedule a growth diagnostic for your students, it will automatically become available to them on the designated publish date.</p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default GrowthDiagnosticFollowup;
